import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import formStyles from '../../styles/SmartSendForm.module.css';
import PhoneVerificationStep from './PhoneVerificationStep';
import CodeVerificationStep from './CodeVerificationStep';
import PersonalMessageStep from './PersonalMessageStep';
import DeliveryFormStep from './DeliveryFormStep';
import AddressSelection from './AddressSelection';
import ReVerificationStep from './ReVerificationStep';
import SessionReVerificationStep from './SessionReVerificationStep';
import ProgressIndicator from './ProgressIndicator';
import ConfirmAddressChangeModal from './ConfirmAddressChangeModal';
import MissingInfoModal from './MissingInfoModal';
import SkuRevealStep from './SkuRevealStep';
import { generateDeepLink as generateDeepLinkAPI } from '../../services/generateDeepLink.api';
import { keepSkuSurprise as keepSkuSurpriseAPI } from '../../services/keepSkuSurprise.api';
import {
  sendVerificationCode as sendVerificationCodeAPI,
  formatPhoneToE164,
  isValidPhoneNumber,
} from '../../services/sendVerificationCode.api';
import { verifyCode as verifyCodeAPI } from '../../services/verifyCode.api';
import { submitDeliveryForm as submitDeliveryFormAPI } from '../../services/submitDeliveryForm.api';
import { getFirstName } from '../../utils/formHelpers';
import { hasValidPersonalMessage } from '../../utils/personalMessage';
import {
  useSmartSend,
  useVerification,
  useAddress,
  useDelivery,
} from '../../contexts/SmartSendContext';
import {
  clearAllSmartSendData,
  saveResendCooldown,
  getResendCooldownRemaining,
  clearResendCooldown,
  savePhoneNumber,
  clearPhoneNumber,
} from '../../utils/localStorage';
import {
  isDevelopment,
  MAX_SMS_RETRIES,
  MAX_CODE_ATTEMPTS,
  ANALYTICS_SOURCE,
} from '../../env/config';
import ErrorMessageWithSupport from '../ErrorMessageWithSupport';
import analytics from '../../../../library/segmentAnalytics';

/**
 * SmartSendFlow - Main orchestrator component for SmartSend delivery flow
 * Manages multi-step process: phone verification → code verification → delivery details
 * Consumes SmartSendContext for shared state management
 * @param {boolean} showContent - Controls visibility based on hero video completion
 * @param {Function} onOrderSubmitted - Called when the order is submitted on the backend (before SKU reveal).
 *   Sets submittedOrderId in parent so AppBar can redirect to order details. Receives orderId as parameter.
 * @param {Function} onSubmissionSuccess - Callback when the full flow completes (after SKU reveal).
 *   Transitions UI to the completion view. Receives orderId as parameter: onSubmissionSuccess(orderId)
 */
const SmartSendFlow = ({ showContent, onOrderSubmitted, onSubmissionSuccess }) => {
  // Access context
  const {
    orderId,
    order: orderData,
    appMode: inApp,
    updateRecipientInfo,
    setPendingRetryCallback,
  } = useSmartSend();

  // Local state for form submission and address editing
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditingAddress, setIsEditingAddress] = useState(false);

  // State for address change confirmation modal
  const [addressChangeModal, setAddressChangeModal] = useState({
    isOpen: false,
    previousAddress: null,
    previousZoningData: null,
  });

  // State for handling backend error scenarios
  const [missingInfoState, setMissingInfoState] = useState({
    show: false,
    fields: [],
    message: '',
    fieldErrors: {}, // Field-specific error messages (e.g., { email: 'That email is already taken' })
  });

  // Persist form data across modal open/close
  const [persistedFormData, setPersistedFormData] = useState({});
  const [reVerificationState, setReVerificationState] = useState({
    show: false,
    message: '',
  });
  const [identityMissingState, setIdentityMissingState] = useState({
    show: false,
    phoneNumber: '',
  });
  const [tokenExpiredState, setTokenExpiredState] = useState({
    show: false,
    phoneNumber: '',
  });
  const [isResendingCode, setIsResendingCode] = useState(false);
  const [showSkuRevealStep, setShowSkuRevealStep] = useState(false);

  // Retry counters (limits imported from centralized config)
  const [smsRetryCount, setSmsRetryCount] = useState(0);
  const [codeAttemptCount, setCodeAttemptCount] = useState(0);

  // Track retry attempts for logging/debugging purposes
  // NOTE: Auto-retry on backend errors has been removed - /complete is only called after user actions
  // This ref is kept for logging purposes and potential future use
  // Using ref instead of state for synchronous access in setTimeout callbacks
  const hasRetriedWithNewNonceRef = useRef(false);

  // Store previous address before validation (for checking if address changed)
  const previousAddressRef = useRef(null);

  // Store the final orderId from submission result for use in completion callbacks
  const finalOrderIdRef = useRef(null);

  // ============================================
  // TEST ERROR TRIGGER - Development only
  // ============================================
  // Press 'E' key to trigger test error (development only)
  const [triggerTestError, setTriggerTestError] = useState(false);

  React.useEffect(() => {
    if (!isDevelopment()) return;

    const handleKeyPress = (e) => {
      // Defense-in-depth: Check development mode at handler execution time
      // This prevents test errors from being triggered if environment changes at runtime
      if (!isDevelopment()) return;

      // Press 'E' key (case-insensitive) to trigger test error
      // Only works when not typing in an input field
      if (
        (e.key === 'E' || e.key === 'e') &&
        e.target.tagName !== 'INPUT' &&
        e.target.tagName !== 'TEXTAREA' &&
        !e.target.isContentEditable
      ) {
        setTriggerTestError(true);
        if (isDevelopment()) {
          console.log(
            '🔍 [SmartSend] Test error triggered via keyboard shortcut (E key)'
          );
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  const {
    verificationStep,
    phoneNumber,
    updatePhoneNumber,
    confirmPhone,
    isLoadingDeepLink,
    setIsLoadingDeepLink,
    isVerifyingCode,
    setIsVerifyingCode,
    isCodeVerified,
    verifyCode: setCodeVerified,
    skipToDeliveryForm,
    continueFromPersonalMessage,
    clearPhoneAndReset,
    isDesktop,
  } = useVerification();

  // Ref for scrollable container to enable scroll-to-top on step transitions
  const contentWrapperRef = useRef(null);

  // Handle invalid pmId case - if we're on personalMessage step but pmId is invalid, skip to complete
  useEffect(() => {
    if (verificationStep === 'personalMessage') {
      // Use shared validation helper to avoid duplicating pmId/personal message rules
      if (!hasValidPersonalMessage(orderData)) {
        continueFromPersonalMessage();
      }
    }
  }, [verificationStep, orderData, continueFromPersonalMessage]);

  const {
    address,
    updateAddress,
    isAddressValidated,
    updateZoningData,
    zoningData,
  } = useAddress();

  const {
    submitDeliveryForm,
    clearDeliveryDetails,
    clearDeliveryDateAndTime,
    deliveryDate,
    deliveryTime,
    deliveryInstructions,
    recipientInfo,
  } = useDelivery();

  // Trigger fade-in animation when content becomes visible
  const [shouldAnimate, setShouldAnimate] = React.useState(false);

  React.useEffect(() => {
    if (showContent && !shouldAnimate) {
      // Small delay to ensure animation triggers after DOM paint
      requestAnimationFrame(() => {
        setShouldAnimate(true);
      });
    }
  }, [showContent, shouldAnimate]);

  // Calculate total steps dynamically based on whether personal message exists
  const hasPersonalMessage = hasValidPersonalMessage(orderData);

  // Calculate current step for progress indicator
  const getCurrentStep = () => {
    // Error states don't show in main progress flow
    if (
      missingInfoState.show ||
      reVerificationState.show ||
      identityMissingState.show ||
      tokenExpiredState.show
    )
      return null;

    if (verificationStep === 'phone') return 1;
    if (verificationStep === 'code') return 2;
    if (verificationStep === 'personalMessage') return 3;
    if (verificationStep === 'complete') {
      // Calculate step numbers based on whether personal message step exists
      const addressStep = hasPersonalMessage ? 4 : 3;
      const deliveryStep = hasPersonalMessage ? 5 : 4;
      const revealStep = hasPersonalMessage ? 6 : 5;

      // Gesture Reveal step: shown after delivery form is submitted
      if (showSkuRevealStep) return revealStep;
      // Delivery form step: Only if address is validated AND delivery form is actually showing
      if (isAddressValidated && !isEditingAddress) return deliveryStep;
      // Address selection step: Either not validated yet or editing
      return addressStep;
    }
    return 1;
  };

  const currentStep = getCurrentStep();
  const totalSteps = hasPersonalMessage ? 6 : 5;
  const stepLabels = [
    'Verify Phone',
    'Enter Code',
    ...(hasPersonalMessage ? ['View Message'] : []),
    'Add Address',
    'Delivery Details',
    'Gesture Reveal',
  ];

  // Track previous step values to detect changes (skip initial mount)
  const previousStepRef = useRef({
    verificationStep,
    isEditingAddress,
    isAddressValidated,
    showSkuRevealStep,
  });
  const isFirstRenderRef = useRef(true);

  // Scroll to top on step transitions (mobile browsers only)
  useEffect(() => {
    // Only apply to mobile browsers
    if (isDesktop) return;

    const scrollContainer = contentWrapperRef.current;
    if (!scrollContainer) return;

    // Skip scroll on initial mount
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      // Update ref with initial values for next comparison
      previousStepRef.current = {
        verificationStep,
        isEditingAddress,
        isAddressValidated,
        showSkuRevealStep,
      };
      return;
    }

    // Check if step actually changed
    const hasStepChanged =
      previousStepRef.current.verificationStep !== verificationStep ||
      previousStepRef.current.isEditingAddress !== isEditingAddress ||
      previousStepRef.current.isAddressValidated !== isAddressValidated ||
      previousStepRef.current.showSkuRevealStep !== showSkuRevealStep;

    // Always update ref for next comparison (even if no change detected)
    previousStepRef.current = {
      verificationStep,
      isEditingAddress,
      isAddressValidated,
      showSkuRevealStep,
    };

    if (!hasStepChanged) return;

    // Use requestAnimationFrame to ensure DOM is ready
    // Wrap in try-catch to handle edge cases where scrollTo might fail
    requestAnimationFrame(() => {
      try {
        if (scrollContainer && typeof scrollContainer.scrollTo === 'function') {
          scrollContainer.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        }
      } catch (error) {
        // Silently handle scroll errors (e.g., if element is removed from DOM)
        if (isDevelopment()) {
          console.debug('[SmartSendFlow] Scroll-to-top error:', error);
        }
      }
    });
  }, [verificationStep, isEditingAddress, isAddressValidated, showSkuRevealStep, isDesktop]);

  // Track modal state for debug panel visibility (development only)
  // Use ref to avoid unnecessary DOM queries on every render
  const isModalOpenRef = useRef(false);

  // Check modal state only in development and only when needed
  useEffect(() => {
    if (!isDevelopment() || !orderData) return;

    const checkModalState = () => {
      try {
        if (typeof document !== 'undefined') {
          isModalOpenRef.current =
            document.querySelector('[role="dialog"][aria-modal="true"]') !== null;
        }
      } catch (error) {
        if (isDevelopment()) {
          console.debug('[SmartSendFlow] Error checking for modal:', error);
        }
        isModalOpenRef.current = false;
      }
    };

    // Check immediately
    checkModalState();

    // Set up MutationObserver to watch for modal changes (more efficient than polling)
    if (typeof document !== 'undefined' && typeof MutationObserver !== 'undefined') {
      const observer = new MutationObserver(checkModalState);
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['role', 'aria-modal'],
      });

      return () => observer.disconnect();
    }
  }, [orderData]); // Re-check when orderData changes

  // Event handlers
  const handlePhoneConfirm = async () => {
    // Validate phone number
    if (!isValidPhoneNumber(phoneNumber)) {
      toast.error('Please enter a valid phone number');
      return;
    }

    // Check resend cooldown to prevent abuse via page refresh
    if (orderId) {
      const cooldownRemaining = getResendCooldownRemaining(orderId);
      if (cooldownRemaining !== null) {
        const minutes = Math.floor(cooldownRemaining / 60);
        const seconds = cooldownRemaining % 60;
        toast.error(
          `Please wait ${minutes}:${seconds
            .toString()
            .padStart(2, '0')} before requesting another code.`,
          { autoClose: 5000 }
        );
        return;
      }
    }

    // Check SMS retry limit
    if (smsRetryCount >= MAX_SMS_RETRIES) {
      toast.error(
        `Maximum SMS attempts reached (${MAX_SMS_RETRIES}). Please try again later.`,
        { autoClose: 5000 }
      );
      return;
    }

    // Format phone to E.164
    const formattedPhone = formatPhoneToE164(phoneNumber);
    updatePhoneNumber(formattedPhone);

    // Save phone number immediately (before sending code) with isPhoneVerified: false
    // This allows persistence across page refreshes
    if (orderId) {
      savePhoneNumber(orderId, formattedPhone, false);
    }

    // Send verification code
    setIsLoadingDeepLink(true);
    try {
      const response = await sendVerificationCodeAPI(formattedPhone);

      if (response.success) {
        setSmsRetryCount((prev) => prev + 1);

        // Start cooldown timer immediately after first code sent
        if (orderId) {
          saveResendCooldown(orderId);
        }

        confirmPhone();
        const remaining = MAX_SMS_RETRIES - (smsRetryCount + 1);
        toast.success(
          `Verification code sent! ${remaining} ${
            remaining === 1 ? 'attempt' : 'attempts'
          } remaining.`
        );
      } else {
        // If code send fails, clear the unverified phone number
        if (orderId) {
          clearPhoneNumber(orderId);
        }
        setSmsRetryCount((prev) => prev + 1);
        toast.error(response.error || 'Failed to send code');
      }
    } catch (error) {
      // If code send fails, clear the unverified phone number
      if (orderId) {
        clearPhoneNumber(orderId);
      }
      console.error('Error sending code:', error);
      setSmsRetryCount((prev) => prev + 1);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsLoadingDeepLink(false);
    }
  };

  const handleCodeVerify = async (code) => {
    // Check code attempt limit
    if (codeAttemptCount >= MAX_CODE_ATTEMPTS) {
      toast.error(
        `Maximum verification attempts reached (${MAX_CODE_ATTEMPTS}). Please request a new code.`,
        { autoClose: 5000 }
      );
      return;
    }

    setIsVerifyingCode(true);
    try {
      const response = await verifyCodeAPI(orderId, phoneNumber, code);

      if (response.success) {
        // Auto-advance desktop users, show button options for mobile users
        setCodeVerified(code, true, { autoAdvanceForDesktop: true });
        setCodeAttemptCount(0); // Reset counter on success

        // Toast message: Desktop users auto-advance, mobile users choose their path
        if (isDesktop) {
          toast.success('Code verified! Taking you to delivery details...');
        } else {
          toast.success(
            'Code verified! Please complete your delivery details.'
          );
        }
      } else {
        setCodeAttemptCount((prev) => prev + 1);
        const remaining = MAX_CODE_ATTEMPTS - (codeAttemptCount + 1);
        toast.error(
          `${response.error || 'Invalid verification code'}. ${remaining} ${
            remaining === 1 ? 'attempt' : 'attempts'
          } remaining.`
        );
        setCodeVerified(null, false);
      }
    } catch (error) {
      console.error('Verification error:', error);
      setCodeAttemptCount((prev) => prev + 1);
      toast.error('Something went wrong. Please try again.');
      setCodeVerified(null, false);
    } finally {
      setIsVerifyingCode(false);
    }
  };

  /**
   * Handles "Redeem in Gesture App" action - generates deep link and redirects to app
   * Ensures skipIntro=true is included to prevent video from playing in app webview
   * @returns {Promise<void>}
   */
  // Helper function to track button clicks
  const trackButtonClick = (buttonName) => {
    if (orderId && buttonName && typeof buttonName === 'string') {
      analytics.track('Any Button Clicked', {
        source: ANALYTICS_SOURCE,
        name: buttonName,
        orderId,
      });
    }
  };

  const handleRedeemInApp = async () => {
    trackButtonClick('Clicked Redeem in App');
    setIsLoadingDeepLink(true);
    try {
      const response = await generateDeepLinkAPI(orderId, phoneNumber);

      if (isDevelopment()) {
        console.log('[Redeem in App] Deep link API response:', {
          success: response.success,
          hasDeepLinkUrl: !!response.deepLinkUrl,
          deepLinkUrl: response.deepLinkUrl,
          error: response.error,
          orderId,
          phoneNumber: phoneNumber
            ? `${phoneNumber.slice(0, 3)}***${phoneNumber.slice(-4)}`
            : null,
        });
      }

      if (response.success && response.deepLinkUrl) {
        // Pause any playing videos before redirect to prevent native player takeover
        try {
          const videoElements = document.querySelectorAll('video');
          videoElements.forEach((video) => {
            if (video && typeof video.pause === 'function') {
              video.pause();
              video.currentTime = 0;
            }
          });
        } catch (videoError) {
          // Non-critical: log but don't block redirect if video pause fails
          if (isDevelopment()) {
            console.warn('Failed to pause video before redirect:', videoError);
          }
        }

        // Deep links are already configured by backend with skipIntro=true
        const deepLinkUrl = response.deepLinkUrl;

        if (isDevelopment()) {
          console.log('[Redeem in App] Deep link URL to open:', deepLinkUrl);
          console.log('[Redeem in App] Full URL details:', {
            url: deepLinkUrl,
            urlType: typeof deepLinkUrl,
            urlLength: deepLinkUrl?.length,
            isEmpty: !deepLinkUrl || deepLinkUrl.trim() === '',
            isValidUrl:
              deepLinkUrl &&
              typeof deepLinkUrl === 'string' &&
              deepLinkUrl.trim() !== '',
          });
        }

        // Validate URL before attempting to open
        if (
          !deepLinkUrl ||
          typeof deepLinkUrl !== 'string' ||
          deepLinkUrl.trim() === ''
        ) {
          console.error('[Redeem in App] Invalid deep link URL:', deepLinkUrl);
          toast.error('Invalid app link. Please try again.');
          return;
        }

        // Try to open the deep link
        try {
          if (isDevelopment()) {
            console.log(
              '[Redeem in App] Attempting to open deep link:',
              deepLinkUrl
            );
          }

          // Copy URL to clipboard for testing (development only)
          if (
            isDevelopment() &&
            navigator.clipboard &&
            navigator.clipboard.writeText
          ) {
            navigator.clipboard
              .writeText(deepLinkUrl)
              .then(() => {
                console.log(
                  '[Redeem in App] Deep link URL copied to clipboard:',
                  deepLinkUrl
                );
              })
              .catch((clipboardError) => {
                console.warn(
                  '[Redeem in App] Failed to copy to clipboard:',
                  clipboardError
                );
              });
          }

          window.location.href = deepLinkUrl;
          toast.success('Opening Gesture app...');
        } catch (redirectError) {
          console.error('[Redeem in App] Failed to redirect:', redirectError);
          toast.error(
            'Unable to open app. Please ensure the Gesture app is installed.'
          );
        }
      } else {
        console.error(
          '[Redeem in App] Failed to generate deep link:',
          response
        );
        toast.error(response.error || 'Failed to generate app link');
      }
    } catch (error) {
      console.error('[Redeem in App] Deep link error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsLoadingDeepLink(false);
    }
  };

  const handleSkip = () => {
    trackButtonClick('Skipped Redeeming in App');
    skipToDeliveryForm();
  };

  /**
   * Handles using different phone in main verification flow
   * Clears cooldown so user can immediately request a code for the new number
   */
  const handleUseDifferentPhone = () => {
    clearPhoneAndReset();
    // Reset retry count for new phone number (clearPhoneAndReset handles cooldown)
    setSmsRetryCount(0);
  };

  /**
   * Handles token refresh request for app mode
   * Stores retry callback and sends refresh_token message to React Native app
   */
  const handleTokenRefreshRequest = () => {
    if (isDevelopment()) {
      console.log(
        '🔄 [Token Refresh] App mode - requesting token refresh with auto-retry'
      );
    }

    // Store retry callback for auto-retry after token refresh
    // Wrap in arrow function to avoid stale closure
    setPendingRetryCallback(() => () => handleDeliveryConfirm());

    toast.error('Your session has expired. Requesting new token...');
    window?.ReactNativeWebView?.postMessage(
      JSON.stringify({ type: 'refresh_token' })
    );
  };

  /**
   * Handles resending verification code
   * @returns {Promise<boolean>} Success status
   */
  const handleResendCode = async () => {
    try {
      const response = await sendVerificationCodeAPI(phoneNumber);

      if (response.success) {
        toast.success('Verification code sent! Please check your messages.');
        return true;
      } else {
        toast.error(response.error || 'Failed to resend code');
        return false;
      }
    } catch (error) {
      console.error('Error resending code:', error);
      toast.error('Something went wrong. Please try again.');
      return false;
    }
  };

  /**
   * Handles submitting missing recipient information
   */
  const handleMissingInfoSubmit = async (missingData) => {
    setIsSubmitting(true);
    try {
      if (isDevelopment()) {
        console.log('🔍 [SmartSend] Submitting missing info:', {
          missingData,
          fieldsBeingSubmitted: Object.keys(missingData),
        });
      }

      // Update context with missing recipient information
      updateRecipientInfo(missingData);

      // Re-submit the form with the complete data (include overrides immediately)
      const result = await submitDeliveryForm(submitDeliveryFormAPI, {
        recipientInfo: missingData,
      });

      if (isDevelopment()) {
        console.log('🔍 [SmartSend] Missing info submission result:', {
          success: result.success,
          code: result.code,
          missing: result.missing,
          message: result.message,
        });
      }

      if (result.success && result.code === 'SUCCESS') {
        // Track submission success
        if (orderId) {
          const payloadSummary = {
            hasAddress: !!address,
            hasDeliveryDate: !!deliveryDate,
            hasDeliveryTime: !!deliveryTime,
            hasDeliveryInstructions: !!deliveryInstructions,
            hasRecipientInfo: true, // Always true since we just submitted it
            missingInfoSubmitted: true,
          };

          analytics.track('SmartSend Submission Success', {
            source: ANALYTICS_SOURCE,
            orderId,
            finalPayloadSummary: payloadSummary,
          });
        }

        // Clear persisted form data on success
        setPersistedFormData({});

        // Close modal and clear state
        setMissingInfoState({
          show: false,
          fields: [],
          message: '',
          fieldErrors: {},
        });

        // The submission was successful - this means the full delivery form was submitted
        // (submitDeliveryForm includes all delivery details + missing recipient info)
        if (isDevelopment()) {
          console.log(
            '✅ [SmartSend] Missing info and delivery form submitted successfully'
          );
        }

        // Use orderId from API response if available, otherwise fall back to context orderId
        const finalOrderId = result.orderId || orderId;
        finalOrderIdRef.current = finalOrderId;

        // Clear all localStorage data after successful submission
        clearAllSmartSendData(finalOrderId);

        // Show SKU reveal step before completion screen
        setShowSkuRevealStep(true);

        // Notify parent that order was submitted (enables AppBar redirect to order details)
        onOrderSubmitted?.(finalOrderId);
      } else if (result.code === 'RECIPIENT_INFO_REQUIRED' && result.missing) {
        // Still missing fields - keep modal open with updated fields
        const normalizedFields = (result.missing || []).map((field) =>
          field === 'phoneNumber' ? 'phone' : field
        );
        setMissingInfoState((prev) => ({
          ...prev,
          fields: normalizedFields,
          message:
            result.message ||
            "Looks like we're missing some details — please update your information.",
          fieldErrors: prev.fieldErrors || {},
        }));
      } else {
        // Handle errors that may occur during missing info submission
        // Check for duplicate errors that should show field-specific errors
        if (result.code === 'EMAIL_DUPLICATE') {
          // Update modal with field-specific error
          setMissingInfoState((prev) => ({
            ...prev,
            fieldErrors: {
              ...prev.fieldErrors,
              email:
                result.message ||
                'That email is already taken. Please use a different email.',
            },
          }));
          toast.error(
            result.message ||
              'That email is already taken. Please use a different email.',
            { autoClose: 5000 }
          );
        } else if (result.code === 'PHONE_DUPLICATE') {
          // Update modal with field-specific error
          setMissingInfoState((prev) => ({
            ...prev,
            fieldErrors: {
              ...prev.fieldErrors,
              phone:
                result.message ||
                'There was an error with your phone number. Please try again or contact support@yourgesture.com.',
            },
          }));
          toast.error(
            result.message ||
              'There was an error with your phone number. Please try again.',
            { autoClose: 5000 }
          );
        } else if (result.code === 'PHONE_VERIFICATION_REQUIRED') {
          // Close modal and show re-verification step
          setMissingInfoState({
            show: false,
            fields: [],
            message: '',
            fieldErrors: {},
          });
          setReVerificationState({
            show: true,
            message:
              result.message ||
              'We need to verify your phone number. An SMS was sent.',
          });
        } else if (result.code === 'TOKEN_EXPIRED') {
          // Close modal and show token expired re-verification step
          setMissingInfoState({
            show: false,
            fields: [],
            message: '',
            fieldErrors: {},
          });
          // Reset verification state since session expired
          setCodeVerified(null, false);
          if (!tokenExpiredState.show) {
            setSmsRetryCount(0);
            setCodeAttemptCount(0);
          }
          const phoneToUse = tokenExpiredState.phoneNumber || phoneNumber || '';
          if (phoneToUse && phoneToUse !== phoneNumber) {
            updatePhoneNumber(phoneToUse);
          }
          setTokenExpiredState({
            show: true,
            phoneNumber: phoneToUse,
          });
        } else if (result.code === 'IDENTITY_MISSING') {
          // Close modal and show identity missing re-verification step
          setMissingInfoState({
            show: false,
            fields: [],
            message: '',
            fieldErrors: {},
          });
          if (inApp) {
            handleTokenRefreshRequest();
          } else {
            setCodeVerified(null, false);
            setIdentityMissingState({
              show: true,
              phoneNumber: phoneNumber || '',
            });
          }
        } else {
          // Generic error - show error message and keep modal open
          toast.error(
            result.error ||
              result.message ||
              'Failed to submit. Please try again.',
            { autoClose: 5000 }
          );
          // Keep modal open so user can retry
        }
      }
    } catch (error) {
      console.error('Error submitting missing info:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handles closing the missing info modal
   * Persists form data so it's available when modal reopens
   */
  const handleCloseMissingInfoModal = () => {
    setMissingInfoState((prev) => ({
      ...prev,
      show: false,
    }));
    // Don't clear persistedFormData - keep it for next time modal opens
  };

  /**
   * Handles form data changes in the modal for persistence
   */
  const handleMissingInfoFormDataChange = (formData) => {
    setPersistedFormData(formData);
  };

  /**
   * Shared completion logic for both SKU reveal and keep-surprise paths.
   * Hides the reveal step, notifies React Native (app mode), and
   * proceeds to the completion screen.
   *
   * @param {string} logContext - Descriptive label for development logging
   * @param {string|null} [finalOrderId] - The authoritative orderId to use, typically from
   *   the submission result. Falls back to context orderId if not provided.
   */
  const proceedToCompletion = async (logContext, finalOrderId) => {
    // Resolve the orderId to use: prefer explicit finalOrderId, then context orderId
    const resolvedOrderId = finalOrderId ?? orderId ?? null;

    // Hide reveal step
    setShowSkuRevealStep(false);

    // Notify React Native app (app mode only)
    if (inApp) {
      window?.ReactNativeWebView?.postMessage(`orderId=${resolvedOrderId || ''}`);
    }

    // Proceed to completion screen
    if (onSubmissionSuccess) {
      if (resolvedOrderId) {
        onSubmissionSuccess(resolvedOrderId);
      } else {
        if (isDevelopment()) {
          console.warn(`[SmartSend] ${logContext} — no orderId available for redirect`);
        }
        onSubmissionSuccess(null);
      }
    }
  };

  /**
   * Handles SKU reveal — proceeds to completion
   * Note: Backend updates isSkuRevealedByRecipient to true automatically when GET /reveal_gesture is called,
   * so no POST call is needed here.
   */
  const handleSkuReveal = async (revealedData) => {
    if (isDevelopment()) {
      console.log('✅ [SmartSend] SKU revealed, proceeding to completion:', revealedData);
    }

    await proceedToCompletion('SKU revealed', finalOrderIdRef.current);
  };

  /**
   * Handles keeping SKU a surprise — marks order as surprise and proceeds to completion
   * Note: No nonce required - this is a one-time action (frontend doesn't show step again),
   * has no external API costs, and ssid is unguessable (UUID v4)
   */
  const handleKeepSurprise = async () => {
    if (isDevelopment()) {
      console.log('✅ [SmartSend] User chose to keep SKU a surprise');
    }

    // Mark SKU as kept a surprise in the backend
    const ssid = orderData?.smartSendId;
    if (ssid) {
      try {
        const result = await keepSkuSurpriseAPI(ssid);

        if (!result.success) {
          if (isDevelopment()) {
            console.warn('⚠️ [SmartSend] Failed to mark SKU as surprise:', result.error);
          }
          // Continue with flow even if API call fails (non-critical)
        }
      } catch (error) {
        if (isDevelopment()) {
          console.error('❌ [SmartSend] Error marking SKU as surprise:', error);
        }
        // Continue with flow even if API call fails (non-critical)
      }
    } else {
      if (isDevelopment()) {
        console.warn('⚠️ [SmartSend] Cannot mark SKU as surprise: SmartSend ID is missing');
      }
      // Continue with flow even if ssid is missing (non-critical)
    }

    await proceedToCompletion('Kept surprise', finalOrderIdRef.current);
  };

  /**
   * Handles re-verification code submission
   */
  const handleReVerify = async (code) => {
    setIsVerifyingCode(true);
    try {
      const response = await verifyCodeAPI(orderId, phoneNumber, code);

      if (response.success) {
        setCodeVerified(code, true, { advanceToComplete: true }); // Update verification code and move forward
        toast.success('Code verified! Resubmitting your delivery details...');

        // Hide re-verification UI
        setReVerificationState({ show: false, message: '' });

        // Automatically retry submission with new code
        // Pass code directly via options to avoid race condition with state update
        setTimeout(() => {
          handleDeliveryConfirmWithCode(code);
        }, 500);
      } else {
        toast.error(response.error || 'Invalid verification code');
      }
    } catch (error) {
      console.error('Re-verification error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsVerifyingCode(false);
    }
  };

  /**
   * Handles phone confirmation for identity missing flow
   * Sends verification code and updates identity missing state
   */
  const handleIdentityMissingPhoneConfirm = async () => {
    // Validate phone number
    if (!isValidPhoneNumber(identityMissingState.phoneNumber)) {
      if (isDevelopment()) {
        console.warn(
          '🔄 [Identity Missing] Invalid phone number:',
          identityMissingState.phoneNumber
        );
      }
      toast.error('Please enter a valid phone number');
      return false;
    }

    // Check resend cooldown
    if (orderId) {
      const cooldownRemaining = getResendCooldownRemaining(orderId);
      if (cooldownRemaining !== null) {
        const minutes = Math.floor(cooldownRemaining / 60);
        const seconds = cooldownRemaining % 60;
        toast.error(
          `Please wait ${minutes}:${seconds
            .toString()
            .padStart(2, '0')} before requesting another code.`,
          { autoClose: 5000 }
        );
        return false;
      }
    }

    // Check SMS retry limit
    if (smsRetryCount >= MAX_SMS_RETRIES) {
      toast.error(
        `Maximum SMS attempts reached (${MAX_SMS_RETRIES}). Please try again later.`,
        { autoClose: 5000 }
      );
      return false;
    }

    // Format phone to E.164
    const formattedPhone = formatPhoneToE164(identityMissingState.phoneNumber);

    if (isDevelopment()) {
      console.log('🔄 [Identity Missing] Phone confirmation started:', {
        phoneNumber: formattedPhone,
        smsRetryCount,
        codeAttemptCount,
      });
    }

    updatePhoneNumber(formattedPhone);

    // Update identity missing state with formatted phone
    setIdentityMissingState((prev) => ({
      ...prev,
      phoneNumber: formattedPhone,
    }));

    // Save phone number with isPhoneVerified: false
    if (orderId) {
      savePhoneNumber(orderId, formattedPhone, false);
    }

    // Send verification code
    setIsLoadingDeepLink(true);
    try {
      const response = await sendVerificationCodeAPI(formattedPhone);

      if (response.success) {
        setSmsRetryCount((prev) => prev + 1);
        if (orderId) {
          saveResendCooldown(orderId);
        }

        // Track phone number entered when user confirms (clicks confirm button)
        if (orderId && formattedPhone) {
          analytics.track('Phone Number Entered', {
            source: ANALYTICS_SOURCE,
            phone: formattedPhone,
            orderId,
          });
        }

        if (isDevelopment()) {
          console.log('✅ [Identity Missing] Code sent successfully:', {
            phoneNumber: formattedPhone,
            smsRetryCount: smsRetryCount + 1,
            remainingAttempts: MAX_SMS_RETRIES - (smsRetryCount + 1),
          });
        }

        toast.success('Verification code sent! Please check your messages.');
        // Return success to trigger code step transition
        return true;
      } else {
        if (isDevelopment()) {
          console.error('❌ [Identity Missing] Failed to send code:', {
            error: response.error,
            phoneNumber: formattedPhone,
          });
        }
        if (orderId) {
          clearPhoneNumber(orderId);
        }
        setSmsRetryCount((prev) => prev + 1);
        toast.error(response.error || 'Failed to send code');
        return false;
      }
    } catch (error) {
      if (orderId) {
        clearPhoneNumber(orderId);
      }
      console.error('Error sending code:', error);
      setSmsRetryCount((prev) => prev + 1);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsLoadingDeepLink(false);
    }
  };

  /**
   * Handles code verification for identity missing flow
   * Verifies code and auto-retries submission
   */
  const handleIdentityMissingCodeVerify = async (code) => {
    if (isDevelopment()) {
      console.log('🔄 [Identity Missing] Code verification started:', {
        code: code.replace(/\d/g, '*'), // Mask code for security
        phoneNumber,
        codeAttemptCount,
        remainingAttempts: MAX_CODE_ATTEMPTS - codeAttemptCount,
      });
    }

    // Check code attempt limit
    if (codeAttemptCount >= MAX_CODE_ATTEMPTS) {
      if (isDevelopment()) {
        console.warn('❌ [Identity Missing] Max code attempts reached:', {
          codeAttemptCount,
          maxAttempts: MAX_CODE_ATTEMPTS,
        });
      }
      toast.error(
        `Maximum verification attempts reached (${MAX_CODE_ATTEMPTS}). Please request a new code.`,
        { autoClose: 5000 }
      );
      return;
    }

    setIsVerifyingCode(true);
    try {
      const response = await verifyCodeAPI(orderId, phoneNumber, code);

      if (response.success) {
        if (isDevelopment()) {
          console.log('✅ [Identity Missing] Code verified successfully:', {
            phoneNumber,
            codeLength: code.length,
            willAutoRetry: true,
          });
        }

        // Save verified code
        setCodeVerified(code, true);
        setCodeAttemptCount(0);
        toast.success('Code verified! Resubmitting your delivery details...');

        // Hide identity missing UI but preserve phone number in case IDENTITY_MISSING occurs again
        // (e.g., if simulation is still enabled or backend still rejects)
        setIdentityMissingState((prev) => ({
          show: false,
          phoneNumber: prev.phoneNumber || phoneNumber || '',
        }));

        if (isDevelopment()) {
          console.log(
            '🔄 [Identity Missing] Hiding step and auto-retrying submission in 500ms'
          );
        }

        // Automatically retry submission with new code
        // Pass code directly via options to avoid race condition with state update
        setTimeout(() => {
          if (isDevelopment()) {
            console.log('🔄 [Identity Missing] Auto-retrying submission...');
          }
          handleDeliveryConfirmWithCode(code);
        }, 500);
      } else {
        if (isDevelopment()) {
          console.warn('❌ [Identity Missing] Code verification failed:', {
            error: response.error,
            codeAttemptCount: codeAttemptCount + 1,
            remainingAttempts: MAX_CODE_ATTEMPTS - (codeAttemptCount + 1),
          });
        }
        setCodeAttemptCount((prev) => prev + 1);
        const remaining = MAX_CODE_ATTEMPTS - (codeAttemptCount + 1);
        toast.error(
          `${response.error || 'Invalid verification code'}. ${remaining} ${
            remaining === 1 ? 'attempt' : 'attempts'
          } remaining.`
        );
      }
    } catch (error) {
      console.error('Verification error:', error);
      setCodeAttemptCount((prev) => prev + 1);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsVerifyingCode(false);
    }
  };

  /**
   * Handles using different phone in identity missing flow
   * Clears cooldown so user can immediately request a code for the new number
   */
  const handleIdentityMissingUseDifferentPhone = () => {
    setIdentityMissingState((prev) => ({ ...prev, phoneNumber: '' }));
    updatePhoneNumber('');
    if (orderId) {
      clearPhoneNumber(orderId);
    }
    // Clear cooldown and reset retry count for new phone number
    if (orderId) {
      clearResendCooldown(orderId);
    }
    setSmsRetryCount(0);
  };

  /**
   * Handles phone confirmation for token expired flow
   * Sends verification code and updates token expired state
   */
  const handleTokenExpiredPhoneConfirm = async () => {
    // Validate phone number
    if (!isValidPhoneNumber(tokenExpiredState.phoneNumber)) {
      if (isDevelopment()) {
        console.warn(
          '🔄 [Token Expired] Invalid phone number:',
          tokenExpiredState.phoneNumber
        );
      }
      toast.error('Please enter a valid phone number');
      return false;
    }

    // Check resend cooldown
    if (orderId) {
      const cooldownRemaining = getResendCooldownRemaining(orderId);
      if (cooldownRemaining !== null) {
        const minutes = Math.floor(cooldownRemaining / 60);
        const seconds = cooldownRemaining % 60;
        toast.error(
          `Please wait ${minutes}:${seconds
            .toString()
            .padStart(2, '0')} before requesting another code.`,
          { autoClose: 5000 }
        );
        return false;
      }
    }

    // Check SMS retry limit
    if (smsRetryCount >= MAX_SMS_RETRIES) {
      toast.error(
        `Maximum SMS attempts reached (${MAX_SMS_RETRIES}). Please try again later.`,
        { autoClose: 5000 }
      );
      return false;
    }

    // Format phone to E.164
    const formattedPhone = formatPhoneToE164(tokenExpiredState.phoneNumber);

    if (isDevelopment()) {
      console.log('🔄 [Token Expired] Phone confirmation started:', {
        phoneNumber: formattedPhone,
        smsRetryCount,
        codeAttemptCount,
      });
    }

    updatePhoneNumber(formattedPhone);

    // Update token expired state with formatted phone
    setTokenExpiredState((prev) => ({
      ...prev,
      phoneNumber: formattedPhone,
    }));

    // Save phone number with isPhoneVerified: false
    if (orderId) {
      savePhoneNumber(orderId, formattedPhone, false);
    }

    // Send verification code
    setIsLoadingDeepLink(true);
    try {
      const response = await sendVerificationCodeAPI(formattedPhone);

      if (response.success) {
        setSmsRetryCount((prev) => prev + 1);
        if (orderId) {
          saveResendCooldown(orderId);
        }

        // Track phone number entered when user confirms (clicks confirm button)
        if (orderId && formattedPhone) {
          analytics.track('Phone Number Entered', {
            source: ANALYTICS_SOURCE,
            phone: formattedPhone,
            orderId,
          });
        }

        if (isDevelopment()) {
          console.log('✅ [Token Expired] Code sent successfully:', {
            phoneNumber: formattedPhone,
            smsRetryCount: smsRetryCount + 1,
            remainingAttempts: MAX_SMS_RETRIES - (smsRetryCount + 1),
          });
        }

        toast.success('Verification code sent! Please check your messages.');
        // Return success to trigger code step transition
        return true;
      } else {
        if (isDevelopment()) {
          console.error('❌ [Token Expired] Failed to send code:', {
            error: response.error,
            phoneNumber: formattedPhone,
          });
        }
        if (orderId) {
          clearPhoneNumber(orderId);
        }
        setSmsRetryCount((prev) => prev + 1);
        toast.error(response.error || 'Failed to send code');
        return false;
      }
    } catch (error) {
      if (orderId) {
        clearPhoneNumber(orderId);
      }
      console.error('Error sending code:', error);
      setSmsRetryCount((prev) => prev + 1);
      toast.error('Something went wrong. Please try again.');
      return false;
    } finally {
      setIsLoadingDeepLink(false);
    }
  };

  /**
   * Handles code verification for token expired flow
   * Verifies code and auto-retries submission
   */
  const handleTokenExpiredCodeVerify = async (code) => {
    if (isDevelopment()) {
      console.log('🔄 [Token Expired] Code verification started:', {
        code: code.replace(/\d/g, '*'), // Mask code for security
        phoneNumber,
        codeAttemptCount,
        remainingAttempts: MAX_CODE_ATTEMPTS - codeAttemptCount,
      });
    }

    // Check code attempt limit
    if (codeAttemptCount >= MAX_CODE_ATTEMPTS) {
      if (isDevelopment()) {
        console.warn('❌ [Token Expired] Max code attempts reached:', {
          codeAttemptCount,
          maxAttempts: MAX_CODE_ATTEMPTS,
        });
      }
      toast.error(
        `Maximum verification attempts reached (${MAX_CODE_ATTEMPTS}). Please request a new code.`,
        { autoClose: 5000 }
      );
      return;
    }

    setIsVerifyingCode(true);
    try {
      const response = await verifyCodeAPI(orderId, phoneNumber, code);

      if (response.success) {
        if (isDevelopment()) {
          console.log('✅ [Token Expired] Code verified successfully:', {
            phoneNumber,
            codeLength: code.length,
            willAutoRetry: true,
          });
        }

        // Save verified code
        setCodeVerified(code, true);
        setCodeAttemptCount(0);
        toast.success('Code verified! Resubmitting your delivery details...');

        // Hide token expired UI but preserve phone number in case TOKEN_EXPIRED occurs again
        // (e.g., if simulation is still enabled or backend still rejecting)
        setTokenExpiredState((prev) => ({
          show: false,
          phoneNumber: prev.phoneNumber || phoneNumber || '',
        }));

        if (isDevelopment()) {
          console.log(
            '🔄 [Token Expired] Hiding step and auto-retrying submission in 500ms'
          );
        }

        // Automatically retry submission with new code
        // Pass code directly via options to avoid race condition with state update
        setTimeout(() => {
          if (isDevelopment()) {
            console.log('🔄 [Token Expired] Auto-retrying submission...');
          }
          handleDeliveryConfirmWithCode(code);
        }, 500);
      } else {
        if (isDevelopment()) {
          console.warn('❌ [Token Expired] Code verification failed:', {
            error: response.error,
            codeAttemptCount: codeAttemptCount + 1,
            remainingAttempts: MAX_CODE_ATTEMPTS - (codeAttemptCount + 1),
          });
        }
        setCodeAttemptCount((prev) => prev + 1);
        const remaining = MAX_CODE_ATTEMPTS - (codeAttemptCount + 1);
        toast.error(
          `${response.error || 'Invalid verification code'}. ${remaining} ${
            remaining === 1 ? 'attempt' : 'attempts'
          } remaining.`
        );
      }
    } catch (error) {
      console.error('Verification error:', error);
      setCodeAttemptCount((prev) => prev + 1);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsVerifyingCode(false);
    }
  };

  /**
   * Handles using different phone in token expired flow
   * Clears cooldown so user can immediately request a code for the new number
   */
  const handleTokenExpiredUseDifferentPhone = () => {
    setTokenExpiredState((prev) => ({ ...prev, phoneNumber: '' }));
    updatePhoneNumber('');
    if (orderId) {
      clearPhoneNumber(orderId);
      // Clear cooldown and reset retry count for new phone number
      clearResendCooldown(orderId);
    }
    setSmsRetryCount(0);
  };

  /**
   * Handles resending verification code
   */
  const handleResendVerificationCode = async () => {
    // Check SMS retry limit
    if (smsRetryCount >= MAX_SMS_RETRIES) {
      toast.error(
        `Maximum SMS attempts reached (${MAX_SMS_RETRIES}). Please try again later.`,
        { autoClose: 5000 }
      );
      return;
    }

    setIsResendingCode(true);
    try {
      const response = await sendVerificationCodeAPI(phoneNumber);

      if (response.success) {
        setSmsRetryCount((prev) => prev + 1);
        setCodeAttemptCount(0); // Reset code attempts when new code is sent
        const remaining = MAX_SMS_RETRIES - (smsRetryCount + 1);
        toast.success(
          `Verification code sent! ${remaining} ${
            remaining === 1 ? 'attempt' : 'attempts'
          } remaining.`
        );
      } else {
        setSmsRetryCount((prev) => prev + 1);
        toast.error(response.error || 'Failed to send code');
      }
    } catch (error) {
      console.error('Error resending code:', error);
      setSmsRetryCount((prev) => prev + 1);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsResendingCode(false);
    }
  };

  /**
   * Handles successful address validation
   */
  const handleAddressValidated = (validatedAddress, newZoningData) => {
    // Address and zoning data are already updated in context by AddressStep

    // If user was editing address, check if address actually changed
    // If it changed, we've already cleared date/time (via modal confirmation)
    // If it didn't change, user just closed the edit modal without changing address
    if (isEditingAddress && previousAddressRef.current && validatedAddress) {
      const previousAddress = previousAddressRef.current;
      const addressChanged =
        previousAddress.lat !== validatedAddress.lat ||
        previousAddress.lng !== validatedAddress.lng ||
        previousAddress.address1 !== validatedAddress.address1;

      if (addressChanged && deliveryDate) {
        // Address changed and date still exists - this shouldn't happen if modal worked correctly
        // But as a safety measure, clear date/time if it still exists
        clearDeliveryDateAndTime();
      }
    }

    // Clear ref after successful validation
    previousAddressRef.current = null;
    setIsEditingAddress(false); // Exit edit mode after successful validation
  };

  /**
   * Handles confirmation of address change
   */
  const handleConfirmAddressChange = () => {
    // Clear date/time (preserve instructions) before entering edit mode
    clearDeliveryDateAndTime();
    // Close modal
    setAddressChangeModal({
      isOpen: false,
      previousAddress: null,
      previousZoningData: null,
    });
    // Now enter edit mode
    setIsEditingAddress(true);
  };

  /**
   * Handles cancellation of address change
   */
  const handleCancelAddressChange = () => {
    // User cancelled - just close the modal
    // No need to revert anything since we haven't entered edit mode yet
    setAddressChangeModal({
      isOpen: false,
      previousAddress: null,
      previousZoningData: null,
    });
    // Clear ref since we're not proceeding
    previousAddressRef.current = null;
    // Don't enter edit mode - user stays on delivery form
  };

  /**
   * Handles opening address edit mode
   */
  const handleOpenAddressEdit = () => {
    // Store current address before entering edit mode
    // This allows us to check if address changed after validation
    previousAddressRef.current = address;

    // Extract displaySendLater from deliveryConfig
    // Pattern matches DeliveryFormStep.jsx for consistency
    const { deliveryConfig = {} } = zoningData || {};
    const { displaySendLater = true } = deliveryConfig;

    // Only show confirmation modal if displaySendLater is true AND a delivery date is selected
    // This is the only scenario where the user has actually selected a date that will be reset
    // When displaySendLater is false, the user never had the option to select a date,
    // so there's nothing to reset and no need for confirmation
    if (displaySendLater && deliveryDate) {
      setAddressChangeModal({
        isOpen: true,
        previousAddress: address,
        previousZoningData: zoningData,
      });
      // Don't enter edit mode yet - wait for user confirmation
      return;
    }

    // No date selected or displaySendLater is false - proceed directly to edit mode
    setIsEditingAddress(true);
  };

  /**
   * Handles cancelling address edit mode
   */
  const handleCancelAddressEdit = () => {
    // Clear ref when cancelling edit mode
    previousAddressRef.current = null;
    setIsEditingAddress(false);
  };

  /**
   * Handles delivery confirmation (wrapper for backward compatibility)
   */
  const handleDeliveryConfirm = async () => {
    return handleDeliveryConfirmWithCode();
  };

  /**
   * Handles delivery confirmation with optional verification code
   * Used when retrying after code verification to avoid race condition with state updates
   * @param {string} verificationCode - Optional verification code to pass directly to submission
   */
  const handleDeliveryConfirmWithCode = async (verificationCode = null) => {
    // Check if there are missing fields that need to be filled first
    // If modal was closed but fields are still missing, reopen it
    if (missingInfoState.fields.length > 0) {
      setMissingInfoState((prev) => ({
        ...prev,
        show: true,
      }));
      return;
    }

    // ============================================
    // TEST ERROR - Development only
    // ============================================
    // Trigger test error via:
    // 1. URL parameter: ?testError=true
    // 2. Keyboard shortcut: Press 'E' key (when not in input field)
    // This simulates a backend error response with support link
    // ⚠️ This code is disabled in production for security - only works in development
    const TEST_ERROR_ENABLED =
      isDevelopment() &&
      (triggerTestError ||
        new URLSearchParams(window.location.search).get('testError') ===
          'true');
    if (TEST_ERROR_ENABLED) {
      // Reset trigger after use
      if (triggerTestError) {
        setTriggerTestError(false);
      }
      setIsSubmitting(true);
      // Simulate API delay
      await new Promise((resolve) => setTimeout(resolve, 500));

      const testErrorResult = {
        success: false,
        code: 'DELIVERY_ZONE_NOT_ALLOWED',
        message:
          'This product cannot be delivered to your selected address. Please contact support@gesture.vip if you need some help.',
        error: 'Delivery zone not allowed for this address',
      };

      // Show error toast with clickable support email link
      toast.error(
        <ErrorMessageWithSupport
          message={testErrorResult.message}
          errorData={{
            orderId,
            orderData,
            errorCode: testErrorResult.code,
            errorMessage: testErrorResult.message,
            phoneNumber: phoneNumber || '',
            address: address || {},
          }}
        />,
        {
          autoClose: 15000, // 15 seconds to allow user to interact
          position: 'top-center',
        }
      );

      if (isDevelopment()) {
        console.log('🔍 [SmartSend] Test error triggered:', {
          code: testErrorResult.code,
          message: testErrorResult.message,
          orderId,
        });
      }

      setIsSubmitting(false);
      return;
    }
    // ============================================
    // END TEST ERROR
    // ============================================

    setIsSubmitting(true);
    try {
      // Check if this is already a retry attempt (using ref for synchronous access)
      const isRetryAttempt = hasRetriedWithNewNonceRef.current;

      // Log before submission (development only)
      if (isDevelopment()) {
        console.log('🔍 [SmartSend] Submitting delivery form:', {
          isRetryAttempt,
          hasRetriedWithNewNonce: hasRetriedWithNewNonceRef.current,
          hasVerificationCode: !!verificationCode,
        });
      }

      // Call context submission function with API callback
      // Pass verification code via options to avoid race condition with state updates
      // Only include verificationCode in options if it's a valid non-empty string
      const options = {};
      if (
        verificationCode &&
        typeof verificationCode === 'string' &&
        verificationCode.trim()
      ) {
        options.verificationCode = verificationCode;
      }
      const result = await submitDeliveryForm(submitDeliveryFormAPI, options);

      // Log the result for debugging (development only)
      if (isDevelopment()) {
        console.log('🔍 [SmartSend] Submission result:', {
          success: result.success,
          code: result.code,
          missing: result.missing,
          message: result.message,
          hasNewNonce: !!result.newNonce,
          fullResult: result,
        });
      }

      if (result.success) {
        // Reset retry flag on success
        hasRetriedWithNewNonceRef.current = false;
        // Check backend response code
        if (result.code === 'SUCCESS') {
          toast.success('Delivery details submitted successfully!');

          // Track submission success
          if (orderId) {
            // Build payload summary
            const payloadSummary = {
              hasAddress: !!address,
              hasDeliveryDate: !!deliveryDate,
              hasDeliveryTime: !!deliveryTime,
              hasDeliveryInstructions: !!deliveryInstructions,
              hasRecipientInfo: !!recipientInfo && (
                recipientInfo.firstName ||
                recipientInfo.lastName ||
                recipientInfo.email ||
                recipientInfo.phone
              ),
            };

            analytics.track('SmartSend Submission Success', {
              source: ANALYTICS_SOURCE,
              orderId,
              finalPayloadSummary: payloadSummary,
            });
          }

          // Use orderId from API response if available, otherwise fall back to context orderId
          const finalOrderId = result.orderId || orderId;
          finalOrderIdRef.current = finalOrderId;

          // Clear all localStorage data after successful submission
          clearAllSmartSendData(finalOrderId);

          // Show SKU reveal step before completion screen
          setShowSkuRevealStep(true);

          // Notify parent that order was submitted (enables AppBar redirect to order details)
          onOrderSubmitted?.(finalOrderId);
        } else {
          // Unexpected success without SUCCESS code
          toast.error('Unexpected response from server. Please try again.');
        }
      } else {
        // ============================================
        // ERROR HANDLING - NO AUTO-RETRY
        // ============================================
        // IMPORTANT: We do NOT auto-retry on backend errors.
        // /complete should only be called after user takes an action:
        //   - User clicks "Confirm" on delivery step
        //   - User submits missing info
        //   - User verifies code (re-verification, identity missing, token expired)
        //   - User triggers token refresh in app mode
        //
        // This prevents /complete from being called automatically without:
        //   - User interaction
        //   - A correct/valid nonce
        //
        // Valid retry scenarios (preserved):
        //   - After code verification: handleReVerify, handleIdentityMissingCodeVerify, handleTokenExpiredCodeVerify
        //   - After token refresh: handleTokenRefreshRequest (app mode only)
        // ============================================

        // Track submission failure (only if orderId exists)
        if (orderId) {
          const backendMessage = result.message || result.error || null;
          analytics.track('SmartSend Submission Failed', {
            source: ANALYTICS_SOURCE,
            orderId,
            error: {
              code: result.code || null,
              backendMessage: backendMessage,
            },
          });
        }

        // Log error for debugging (development only)
        if (isDevelopment()) {
          console.log('⚠️ [SmartSend] Backend error received:', {
            errorCode: result.code,
            hasNewNonce: !!result.newNonce,
            message: result.message,
            note: 'User action required - no auto-retry',
          });
        }

        // Handle specific backend error codes
        switch (result.code) {
          case 'IDENTITY_MISSING':
            // Token and code are missing - need to refresh token
            if (isDevelopment()) {
              console.log(
                '🔍 [Identity Missing] IDENTITY_MISSING error received:',
                {
                  inApp,
                  currentPhoneNumber: phoneNumber,
                  verificationStep,
                  isAddressValidated,
                }
              );
            }

            if (inApp) {
              handleTokenRefreshRequest();
            } else {
              // For web users, show identity missing step (phone input modal)
              // This allows re-verification without resetting the entire flow
              if (isDevelopment()) {
                console.log(
                  '🔄 [Identity Missing] Web mode - showing SessionReVerificationStep:',
                  {
                    phoneNumber: phoneNumber || '',
                    isCodeVerified,
                    willShowStep: true,
                    existingIdentityMissingPhone:
                      identityMissingState.phoneNumber,
                  }
                );
              }

              // Reset verification state since session expired
              // Clear the verified code and status to force re-verification
              setCodeVerified(null, false);

              // If this is the first time showing SessionReVerificationStep, reset counters
              // If it's showing again (e.g., after auto-retry failed), preserve counters
              if (!identityMissingState.show) {
                setSmsRetryCount(0);
                setCodeAttemptCount(0);
              }

              // Preserve phone number if we already have one from previous identity missing flow
              // Otherwise start fresh
              const phoneToUse =
                identityMissingState.phoneNumber || phoneNumber || '';

              // Update context phone number if we have one to preserve
              if (phoneToUse && phoneToUse !== phoneNumber) {
                updatePhoneNumber(phoneToUse);
              }

              setIdentityMissingState({
                show: true,
                phoneNumber: phoneToUse,
              });
              // Clear any other error states
              setMissingInfoState({
                show: false,
                fields: [],
                message: '',
                fieldErrors: {},
              });
              setReVerificationState({ show: false, message: '' });
              setTokenExpiredState({ show: false, phoneNumber: '' });
            }
            break;

          case 'TOKEN_EXPIRED':
            // TokenId has expired or is invalid
            if (isDevelopment()) {
              console.log('🔍 [Token Expired] TOKEN_EXPIRED error received:', {
                inApp,
                currentPhoneNumber: phoneNumber,
                verificationStep,
                isAddressValidated,
              });
            }

            if (inApp) {
              handleTokenRefreshRequest();
            } else {
              // For web users, show session re-verification step (phone input modal)
              // This allows re-verification without resetting the entire flow
              if (isDevelopment()) {
                console.log(
                  '🔄 [Token Expired] Web mode - showing SessionReVerificationStep:',
                  {
                    phoneNumber: phoneNumber || '',
                    isCodeVerified,
                    willShowStep: true,
                    existingTokenExpiredPhone: tokenExpiredState.phoneNumber,
                  }
                );
              }

              // Reset verification state since session expired
              // Clear the verified code and status to force re-verification
              setCodeVerified(null, false);

              // If this is the first time showing SessionReVerificationStep, reset counters
              // If it's showing again (e.g., after auto-retry failed), preserve counters
              if (!tokenExpiredState.show) {
                setSmsRetryCount(0);
                setCodeAttemptCount(0);
              }

              // Preserve phone number if we already have one from previous token expired flow
              // Otherwise start fresh
              const phoneToUse =
                tokenExpiredState.phoneNumber || phoneNumber || '';

              // Update context phone number if we have one to preserve
              if (phoneToUse && phoneToUse !== phoneNumber) {
                updatePhoneNumber(phoneToUse);
              }

              setTokenExpiredState({
                show: true,
                phoneNumber: phoneToUse,
              });
              // Clear any other error states
              setMissingInfoState({
                show: false,
                fields: [],
                message: '',
                fieldErrors: {},
              });
              setReVerificationState({ show: false, message: '' });
              setIdentityMissingState({ show: false, phoneNumber: '' });
            }
            break;

          case 'RECIPIENT_INFO_REQUIRED':
            // Missing recipient information - open modal with missing info form
            // Normalize field names: ensure phoneNumber is mapped to phone
            const normalizedFields = (
              result.missing || ['firstName', 'lastName', 'email', 'phone']
            ).map((field) => (field === 'phoneNumber' ? 'phone' : field));

            if (isDevelopment()) {
              console.log(
                '🔍 [SmartSend] RECIPIENT_INFO_REQUIRED - Opening missing info modal:',
                {
                  original: result.missing,
                  normalized: normalizedFields,
                }
              );
            }

            setMissingInfoState({
              show: true,
              fields: normalizedFields,
              message:
                result.message ||
                "Looks like we're missing some details — please update your information.",
              fieldErrors: {},
            });
            break;

          case 'PHONE_VERIFICATION_REQUIRED':
            // Deep link code has expired - need to re-verify phone
            setReVerificationState({
              show: true,
              message:
                result.message ||
                'We need to verify your phone number. An SMS was sent.',
            });
            break;

          case 'ORDER_CANCELLED':
            // Order was cancelled/inactive (e.g., goody_inactive cause)
            if (isDevelopment()) {
              console.log('🔍 [SmartSend] ORDER_CANCELLED error received:', {
                message: result.message,
                cause: result.data?.cause,
              });
            }

            toast.error(
              result.message ||
                'This order is no longer available. Please check for future notifications.',
              { autoClose: 8000 }
            );

            // Clear all form state
            clearDeliveryDetails();
            setMissingInfoState({
              show: false,
              fields: [],
              message: '',
              fieldErrors: {},
            });
            setReVerificationState({ show: false, message: '' });
            setIdentityMissingState({ show: false, phoneNumber: '' });
            setTokenExpiredState({ show: false, phoneNumber: '' });
            break;

          case 'PAST_DELIVERY_DATE':
          case 'INVALID_DELIVERY_DATE':
          case 'MISSING_DELIVERY_DATE':
            // Delivery date validation errors
            if (isDevelopment()) {
              console.log('🔍 [SmartSend] Delivery date error received:', {
                code: result.code,
                message: result.message,
              });
            }

            toast.error(
              result.message ||
                'Please select a valid delivery date and try again.',
              { autoClose: 5000 }
            );

            // Clear delivery date/time to force re-selection
            clearDeliveryDetails();

            // Clear any other error states that might conflict
            setMissingInfoState({
              show: false,
              fields: [],
              message: '',
              fieldErrors: {},
            });
            setReVerificationState({ show: false, message: '' });
            break;

          case 'EMAIL_DUPLICATE':
            // Email already exists
            if (isDevelopment()) {
              console.log('🔍 [SmartSend] EMAIL_DUPLICATE error received:', {
                message: result.message,
                missingInfoStateShowing: missingInfoState.show,
              });
            }

            // If modal is showing, update it with field-specific error
            if (missingInfoState.show) {
              setMissingInfoState((prev) => ({
                ...prev,
                fieldErrors: {
                  ...prev.fieldErrors,
                  email:
                    result.message ||
                    'That email is already taken. Please use a different email.',
                },
              }));

              // Also show toast for immediate feedback
              toast.error(
                result.message ||
                  'That email is already taken. Please use a different email.',
                { autoClose: 5000 }
              );
            } else {
              // If not showing, show as generic error and open modal
              toast.error(
                result.message ||
                  'That email is already taken. Please use a different email.',
                { autoClose: 5000 }
              );

              // Open modal with email field highlighted
              setMissingInfoState({
                show: true,
                fields: ['email'],
                message:
                  result.message ||
                  'That email is already taken. Please enter a different email address.',
                fieldErrors: {
                  email:
                    result.message ||
                    'That email is already taken. Please use a different email.',
                },
              });
            }
            break;

          case 'PHONE_DUPLICATE':
            // Phone number already exists
            if (isDevelopment()) {
              console.log('🔍 [SmartSend] PHONE_DUPLICATE error received:', {
                message: result.message,
                missingInfoStateShowing: missingInfoState.show,
              });
            }

            // If modal is showing, update it with field-specific error
            if (missingInfoState.show) {
              setMissingInfoState((prev) => ({
                ...prev,
                fieldErrors: {
                  ...prev.fieldErrors,
                  phone:
                    result.message ||
                    'There was an error with your phone number. Please try again or contact support@yourgesture.com.',
                },
              }));

              // Also show toast for immediate feedback
              toast.error(
                result.message ||
                  'There was an error with your phone number. Please try again.',
                { autoClose: 5000 }
              );
            } else {
              // If not showing, show toast and open modal with phone field to allow user to fix
              toast.error(
                result.message ||
                  'There was an error with your phone number. Please try again.',
                { autoClose: 5000 }
              );

              // Open modal with phone field highlighted
              setMissingInfoState({
                show: true,
                fields: ['phone'],
                message:
                  result.message ||
                  'There was an error with your phone number. Please enter a different phone number.',
                fieldErrors: {
                  phone:
                    result.message ||
                    'There was an error with your phone number. Please try again or contact support@yourgesture.com.',
                },
              });
            }
            break;

          case 'CODE_FORMAT':
            // Verification code format error
            if (isDevelopment()) {
              console.log('🔍 [SmartSend] CODE_FORMAT error received:', {
                message: result.message,
                verificationStep,
              });
            }

            const codeFormatMessage =
              result.message ||
              'Please enter a valid verification code. Click "Send SMS" to get a new code.';

            toast.error(
              <ErrorMessageWithSupport
                message={
                  codeFormatMessage.includes('support@gesture.vip')
                    ? codeFormatMessage
                    : `${codeFormatMessage} Please contact support@gesture.vip if you need some help.`
                }
                errorData={{
                  orderId,
                  orderData,
                  errorCode: result.code,
                  errorMessage: result.message || codeFormatMessage,
                  phoneNumber: phoneNumber || '',
                  address: address || {},
                  context: 'Verification code format error',
                }}
              />,
              { autoClose: 10000, position: 'top-center' }
            );

            // If in code verification step, reset code verification state
            if (verificationStep === 'code') {
              setCodeVerified(null, false);
              setCodeAttemptCount(0);
            }

            // Clear any other error states
            setMissingInfoState({
              show: false,
              fields: [],
              message: '',
              fieldErrors: {},
            });
            setReVerificationState({ show: false, message: '' });
            break;

          default:
            // Check for ORDER_CANCELLED via cause in data (backend pattern)
            if (
              result.data?.cause === 'goody_inactive' ||
              (result.message &&
                result.message.includes('change in this order'))
            ) {
              // Order was cancelled due to goody being inactive
              if (isDevelopment()) {
                console.log(
                  '🔍 [SmartSend] ORDER_CANCELLED detected via cause/message:',
                  {
                    cause: result.data?.cause,
                    message: result.message,
                  }
                );
              }

              toast.error(
                result.message ||
                  'This order is no longer available. Please check for future notifications.',
                { autoClose: 8000 }
              );

              // Clear all form state
              clearDeliveryDetails();
              setMissingInfoState({
                show: false,
                fields: [],
                message: '',
                fieldErrors: {},
              });
              setReVerificationState({ show: false, message: '' });
              setIdentityMissingState({ show: false, phoneNumber: '' });
              setTokenExpiredState({ show: false, phoneNumber: '' });
            } else {
              // Generic error - check for specific error messages in result.error
              const errorMessage = result.error || result.message;
              let userMessage = 'Failed to submit. Please try again.';

              // Handle message-based error codes (backend returns in message field)
              if (errorMessage) {
                if (errorMessage.includes('VERIFICATION_ERR')) {
                  userMessage =
                    'The verification code is incorrect. Please enter the correct code and try again. Please contact support@gesture.vip if you need some help.';
                } else if (
                  errorMessage.includes('EMAIL_DUPLICATE') ||
                  errorMessage.includes('email is already taken')
                ) {
                  userMessage =
                    'That email is already taken. Please use a different email. Please contact support@gesture.vip if you need some help.';
                } else if (
                  errorMessage.includes('PHONE_DUPLICATE') ||
                  errorMessage.includes('error with your phone')
                ) {
                  userMessage =
                    'There was an error with your phone number. Please try again or contact support@gesture.vip if you need some help.';
                } else if (
                  errorMessage.includes('PHONE_FORMAT') ||
                  errorMessage.includes('phone format')
                ) {
                  userMessage =
                    'There was an error with your phone format. Please try again or contact support@gesture.vip if you need some help.';
                } else if (
                  errorMessage.includes('CODE_FORMAT') ||
                  errorMessage.includes('valid verification code')
                ) {
                  userMessage =
                    'Please enter a valid verification code. Click "Send SMS" to get a new code. Please contact support@gesture.vip if you need some help.';
                } else if (
                  errorMessage.includes('SKU_EXPIRED') ||
                  errorMessage.includes('not available for delivery')
                ) {
                  userMessage =
                    'This product is not available for delivery. Please contact support@gesture.vip if you need some help.';
                } else if (
                  errorMessage.includes('DELIVERY_ZONE_NOT_ALLOWED') ||
                  errorMessage.includes('not deliverable to')
                ) {
                  userMessage =
                    'This product is not deliverable to the provided address. Please contact support@gesture.vip if you need some help.';
                } else {
                  // Use backend message if available, otherwise use generic
                  // Add support email if not already present
                  if (errorMessage.includes('support@gesture.vip')) {
                    userMessage = errorMessage;
                  } else {
                    // Ensure proper punctuation before appending support text
                    const trimmedMessage = errorMessage.trim();
                    const endsWithPunctuation = /[.!?]$/.test(trimmedMessage);
                    const separator = endsWithPunctuation ? ' ' : '. ';
                    userMessage = `${trimmedMessage}${separator}Please contact support@gesture.vip if you need some help.`;
                  }
                }
              } else {
                // No error message - add support email
                userMessage =
                  'Failed to submit. Please try again. Please contact support@gesture.vip if you need some help.';
              }

              // Show error toast with clickable support email link
              toast.error(
                <ErrorMessageWithSupport
                  message={userMessage}
                  errorData={{
                    orderId,
                    orderData,
                    errorCode: result.code,
                    errorMessage: errorMessage || userMessage,
                    phoneNumber: phoneNumber || '',
                    address: address || {},
                  }}
                />,
                {
                  autoClose: 15000, // 15 seconds to allow user to interact
                  position: 'top-center',
                }
              );
            }
        }

        // Reset retry flag after processing error (for next submission attempt)
        // This ensures the flag is clean for the next manual retry
        hasRetriedWithNewNonceRef.current = false;
      }
    } catch (error) {
      console.error('Unexpected error during submission:', error);

      // Show error with support email link for unexpected errors
      const errorMessage =
        error?.message || 'Something went wrong. Please try again.';
      const userMessage = errorMessage.includes('support@gesture.vip')
        ? errorMessage
        : `${errorMessage} Please contact support@gesture.vip if you need some help.`;

      toast.error(
        <ErrorMessageWithSupport
          message={userMessage}
          errorData={{
            orderId,
            orderData,
            errorCode: error?.code || 'UNEXPECTED_ERROR',
            errorMessage: errorMessage,
            phoneNumber: phoneNumber || '',
            address: address || {},
            context: 'Unexpected error during form submission',
          }}
        />,
        {
          autoClose: 15000,
          position: 'top-center',
        }
      );

      // Reset retry flag on exception
      hasRetriedWithNewNonceRef.current = false;
    } finally {
      setIsSubmitting(false);
    }
  };


  if (!showContent) return null;

  const renderDebugPanel = () => {
    if (!isDevelopment() || !orderData) {
      return null;
    }

    // Hide debug panel when DatePickerModal is open
    // Modal state is tracked via MutationObserver in useEffect above
    if (isModalOpenRef.current) {
      return null;
    }

    return (
      <div className={formStyles.debugWrapper}>
        <div className={`${formStyles.card} ${formStyles.debugCard}`}>
          <details>
            <summary className={formStyles.debugSummary}>
              🛠️ Debug: Verification State
            </summary>
            <div className={formStyles.debugContent}>
              <h4>Current State:</h4>
              <pre>
                {JSON.stringify(
                  {
                    verificationStep,
                    phoneNumber,
                    inApp,
                    isCodeVerified,
                    isAddressValidated,
                    shouldShowPhone: !inApp && verificationStep === 'phone',
                    shouldShowCode: !inApp && verificationStep === 'code',
                    shouldShowAddress:
                      verificationStep === 'complete' && !isAddressValidated,
                  },
                  null,
                  2
                )}
              </pre>
              <button
                onClick={() => {
                  clearAllSmartSendData(orderId);
                  window.location.reload();
                }}
                className={formStyles.debugClearButton}
              >
                🗑️ Clear localStorage & Reload
              </button>
            </div>
          </details>
        </div>
      </div>
    );
  };

  /**
   * Renders the appropriate verification step component
   * @returns {JSX.Element|null} Phone or code verification component based on current step
   */
  const renderVerificationStep = () => {
    if (verificationStep === 'phone') {
      return (
        <PhoneVerificationStep
          phoneNumber={phoneNumber}
          onPhoneChange={updatePhoneNumber}
          onConfirm={handlePhoneConfirm}
          isLoading={isLoadingDeepLink}
          orderId={orderId}
        />
      );
    }

    if (verificationStep === 'code') {
      return (
        <CodeVerificationStep
          phoneNumber={phoneNumber}
          onVerify={handleCodeVerify}
          onRedeemInApp={handleRedeemInApp}
          onSkip={handleSkip}
          onResendCode={handleResendCode}
          onUseDifferentPhone={handleUseDifferentPhone}
          isLoadingDeepLink={isLoadingDeepLink}
          isVerifyingCode={isVerifyingCode}
          isCodeVerified={isCodeVerified}
          isDesktop={isDesktop}
          orderId={orderId}
        />
      );
    }

    if (verificationStep === 'personalMessage') {
      // Only render if pmId is valid
      if (hasValidPersonalMessage(orderData)) {
        return (
          <PersonalMessageStep
            pmId={orderData.pmId}
            onContinue={continueFromPersonalMessage}
          />
        );
      }
      // If pmId is invalid, don't render (shouldn't happen, but defensive)
      return null;
    }

    return null;
  };

  return (
    <>
      <div
        ref={contentWrapperRef}
        className={`${formStyles.contentWrapper} ${
          shouldAnimate ? formStyles.fadeIn : ''
        } ${inApp ? formStyles.inApp : ''}`}
      >
        {/* Verification Steps - Special layout with text at top, cards centered */}
        {!inApp && verificationStep !== 'complete' && (
          <>
            {/* Header Section - Verification Text */}
            {/* Hide header when showing personal message */}
            {verificationStep !== 'personalMessage' && (
              <header className={formStyles.verificationHeader}>
                <h1 className={formStyles.headline}>
                  {orderData?.senderName
                    ? `${getFirstName(orderData.senderName)} sent you a gift!`
                    : "You've received a special gift via Gesture!"}
                </h1>

                {verificationStep === 'phone' && (
                  <p className={formStyles.appPromoText}>
                    The experience is better in the Gesture app.
                  </p>
                )}

                <p className={formStyles.subheadline}>
                  {verificationStep === 'phone'
                    ? "Let's check if you're a Gesture user."
                    : 'Please verify your phone number.'}
                </p>
              </header>
            )}

            {/* Main Section - Progress + Verification Forms */}
            <div className={formStyles.verificationMain}>
              {currentStep && (
                <ProgressIndicator
                  currentStep={currentStep}
                  totalSteps={totalSteps}
                  stepLabels={stepLabels}
                />
              )}
              {renderVerificationStep()}
            </div>
          </>
        )}

        {/* In-App users - Direct rendering with progress indicator */}
        {inApp && verificationStep !== 'complete' && (
          <div className={formStyles.verificationMain}>
            {currentStep && (
              <ProgressIndicator
                currentStep={currentStep}
                totalSteps={totalSteps}
                stepLabels={stepLabels}
              />
            )}
            {renderVerificationStep()}
          </div>
        )}

        {/* Progress Indicator - Show for address and delivery steps */}
        {currentStep && verificationStep === 'complete' && (
          <ProgressIndicator
            currentStep={currentStep}
            totalSteps={totalSteps}
            stepLabels={stepLabels}
          />
        )}

        {/* Step 3: Address Selection and Validation - Show until validated OR when editing */}
        {verificationStep === 'complete' &&
          (!isAddressValidated || isEditingAddress) && (
            <AddressSelection
              order={orderData}
              orderId={orderId}
              address={address}
              onAddressValidated={handleAddressValidated}
              onAddressChange={updateAddress}
              onZoningDataChange={updateZoningData}
              onCancelEdit={handleCancelAddressEdit}
            />
          )}

        {/* Step 4: Delivery Date/Time Section - Show after validated AND not editing */}
        {verificationStep === 'complete' &&
          isAddressValidated &&
          !isEditingAddress &&
          !reVerificationState.show &&
          !identityMissingState.show &&
          !tokenExpiredState.show &&
          !showSkuRevealStep && (
            <DeliveryFormStep
              onAddressEdit={handleOpenAddressEdit}
              onConfirm={handleDeliveryConfirm}
              isSubmitting={isSubmitting}
            />
          )}

        {/* Step 5/6: Gesture Reveal - Show after successful delivery submission */}
        {showSkuRevealStep && (
          <SkuRevealStep
            onReveal={handleSkuReveal}
            onKeepSurprise={handleKeepSurprise}
          />
        )}

        {/* Step 6: Re-Verification - Show when phone verification is required again */}
        {verificationStep === 'complete' &&
          isAddressValidated &&
          reVerificationState.show && (
            <ReVerificationStep
              message={reVerificationState.message}
              onVerify={handleReVerify}
              onResendCode={handleResendVerificationCode}
              isVerifying={isVerifyingCode}
              isResending={isResendingCode}
            />
          )}

        {/* Step 7: Identity Missing - Show when IDENTITY_MISSING error occurs (web users only) */}
        {verificationStep === 'complete' &&
          isAddressValidated &&
          identityMissingState.show && (
            <SessionReVerificationStep
              phoneNumber={identityMissingState.phoneNumber || ''}
              initialPhoneNumber={identityMissingState.phoneNumber || ''}
              onPhoneChange={(newPhone) =>
                setIdentityMissingState((prev) => ({
                  ...prev,
                  phoneNumber: newPhone,
                }))
              }
              onPhoneConfirm={handleIdentityMissingPhoneConfirm}
              onCodeVerify={handleIdentityMissingCodeVerify}
              onResendCode={handleResendCode}
              onUseDifferentPhone={handleIdentityMissingUseDifferentPhone}
              isLoadingCode={isLoadingDeepLink}
              isVerifyingCode={isVerifyingCode}
              isCodeVerified={false}
              errorType="IDENTITY_MISSING"
              isDesktop={isDesktop}
              orderId={orderId}
            />
          )}

        {/* Step 8: Token Expired - Show when TOKEN_EXPIRED error occurs (web users only) */}
        {verificationStep === 'complete' &&
          isAddressValidated &&
          tokenExpiredState.show && (
            <SessionReVerificationStep
              phoneNumber={tokenExpiredState.phoneNumber || ''}
              initialPhoneNumber={tokenExpiredState.phoneNumber || ''}
              onPhoneChange={(newPhone) =>
                setTokenExpiredState((prev) => ({
                  ...prev,
                  phoneNumber: newPhone,
                }))
              }
              onPhoneConfirm={handleTokenExpiredPhoneConfirm}
              onCodeVerify={handleTokenExpiredCodeVerify}
              onResendCode={handleResendCode}
              onUseDifferentPhone={handleTokenExpiredUseDifferentPhone}
              isLoadingCode={isLoadingDeepLink}
              isVerifyingCode={isVerifyingCode}
              isCodeVerified={false}
              errorType="TOKEN_EXPIRED"
              isDesktop={isDesktop}
              orderId={orderId}
            />
          )}

        {/* Address Change Confirmation Modal */}
        <ConfirmAddressChangeModal
          isOpen={addressChangeModal.isOpen}
          onConfirm={handleConfirmAddressChange}
          onCancel={handleCancelAddressChange}
        />

        {/* Missing Info Modal */}
        <MissingInfoModal
          isOpen={missingInfoState.show}
          missingFields={missingInfoState.fields}
          message={missingInfoState.message}
          onSubmit={handleMissingInfoSubmit}
          isSubmitting={isSubmitting}
          fieldErrors={missingInfoState.fieldErrors || {}}
          onClose={handleCloseMissingInfoModal}
          initialFormData={persistedFormData}
          onFormDataChange={handleMissingInfoFormDataChange}
        />
      </div>

      {renderDebugPanel()}
    </>
  );
};

export default SmartSendFlow;
